import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Tooltip,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSharedAuth } from "../contexts/AuthProvider";
import { t } from "@lingui/macro";

const StoryCard = ({ story }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, customClaims } = useSharedAuth();

  const handleNavigate = () => {
    if (customClaims?.isEditor) {
      navigate(`/editorial/stories/${story.id}`);
    } else {
      navigate(`/mystories/${story.id}`);
    }
  };

  return (
    <Card
      sx={{
        position: "relative", // Needed for absolute positioning of status tag
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        boxShadow: theme.shadows[5],
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        "&:hover": { 
          boxShadow: theme.shadows[8], 
          transform: "scale(1.05)"
        },
        margin: 0,
        padding: 0,
      }}
    >
      {/* Status Indicator */}
      <Box
        sx={{
          position: "absolute",
          top: theme.spacing(0.5),
          right: theme.spacing(0.5),
          backgroundColor: story.isPublished
            ? theme.palette.success.main // Green for published
            : theme.palette.warning.main, // Gold for draft
          borderRadius: 15,
          paddingX: 0.5,
          paddingY: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: theme.shadows[2],
          zIndex: 2,
          minWidth: "40px", // Ensure a minimum width for visibility
          border: "1px solid white", // ✅ White border for contrast
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: 8,
            fontWeight: theme.typography.fontWeightBold,
            color: "white",
            textTransform: "uppercase",
            textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
            margin: 0.25,             // Reset any default margins
            lineHeight: 1,         // Adjust line height
          }}
        >
          {story.isPublished
            ? t({ message: "PUBLISHED" })
            : t({ message: "DRAFT" })}
        </Typography>
      </Box>

      <Tooltip
        title={story.description || t({ message: "No description available" })}
        placement="top"
        arrow
      >
        <CardActionArea
          onClick={handleNavigate}
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Cover Image */}
          <CardMedia
            component="img"
            image={
              story.coverImageUrl ||
              story.coverUrl ||
              "/book_cover_placeholder.webp"
            }
            alt={t({ message: "{title} Cover", values: { title: story.title } })}
            sx={{ height: "100%", width: "100%", objectFit: "cover" }}
            onError={(e) => {
              if (
                e.target.src !==
                `${window.location.origin}/book_cover_placeholder.webp`
              ) {
                e.target.src = "/book_cover_placeholder.webp";
              }
            }}
          />

          {/* Card Content */}
          <CardContent
            sx={{
              height: "20%",
              width: "100%",
              padding: theme.spacing(0.5),
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
              paddingLeft: theme.spacing(0.5),
              paddingRight: 0,
            }}
          >
            {/* Title */}
            <Typography
              variant="h7"
              component="div"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
            >
              {story.title}
            </Typography>

            {/* Author Name */}
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
            >
              {story.writerName || t({ message: "Unknown Author" })}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Tooltip>
    </Card>
  );
};

export default StoryCard;
